import { Action } from "redux"

import * as actions from "./actions"

import { guid } from "../../util/Guid"
import {
    workspaceConceptUrl,
    workspaceCoverPageUrl,
    workspaceItemUrl,
} from "../../util/UrlGenerator"
import { IBreadcrumbInfo, IHeader } from "./index"
import { IButton } from "../common/contextmenu"

export const initialState: IHeader = {
    resource: {
        title: {
            value: "",
            label: "",
            readOnly: true,
            hidden: true,
        },
        bookmarkButtonId: undefined,
        workspaceId: -1,
    },
    activePanel: "",
    breadcrumbs: {},
    buttons: {},
    buttonsVisibility: {},
    changeViewData: {},
    headerTypes: [],
    shareSuccessMessageVisible: false,
    shouldShowBookmarkButton: false,
    shouldShowCondensed: false,
    shouldShowFlowPanelSwitch: false,
    shouldShowHiddenBreadcrumbs: false,
    unsyncTitle: "",
}

const ACTION_HANDLERS: {
    [action: string]: (state: IHeader, props: Action) => IHeader
} = {
    [actions.INITIALIZE]: (
        state,
        {
            props,
            activePanel,
            buttonsArray,
            breadcrumbsArray,
        }: actions.Initialize,
    ): IHeader => {
        const buttons: Record<string, IButton> = {}
        const buttonsVisibility: Record<string, boolean> = {
            ...state.buttonsVisibility,
        }

        if (!!buttonsArray) {
            buttonsArray.forEach((b) => {
                const uuid = guid()
                if (!!b.id) {
                    buttonsVisibility[b.id] = true
                }

                buttons[uuid] = {
                    ...b,
                    isLoading: false,
                    uuid,
                }
            })
        }

        const breadcrumbs: Record<string, IBreadcrumbInfo> = {}
        if (!!breadcrumbsArray) {
            breadcrumbsArray.forEach((b, i) => {
                const uuid = guid()
                breadcrumbs[uuid] = {
                    uuid,
                    index: i,
                    breadcrumb: b,
                }
            })
        }
        const unsyncTitle = props.resource?.title.value ?? ""

        return {
            ...state,
            ...props,
            activePanel,
            buttons,
            buttonsVisibility,
            breadcrumbs,
            unsyncTitle,
        }
    },

    [actions.REFRESH_TITLE]: (
        state,
        { fieldValue }: actions.RefreshTitle,
    ): IHeader => ({
        ...state,
        unsyncTitle: fieldValue.value ?? state.unsyncTitle,
        resource: {
            ...state.resource,
            title: { ...state.resource.title, ...fieldValue },
        },
    }),

    [actions.UPDATE_UNSYNC_TITLE]: (
        state,
        { title }: actions.UpdateTitle,
    ): IHeader => ({
        ...state,
        unsyncTitle: title,
    }),

    [actions.REFRESH_STATUS]: (
        state,
        { statusLabel }: actions.RefreshStatus,
    ): IHeader => ({
        ...state,
        resource: { ...state.resource, statusLabel },
    }),

    [actions.REFRESH_CONCEPT_BREADCRUMB]: (
        state,
        {
            conceptId,
            conceptName,
            workspaceId,
            workspaceName,
        }: actions.RefreshConceptBreadcrumb,
    ): IHeader => {
        const resourceId = state.resource.id
        if (!resourceId) {
            return state
        }

        const oldBreadcrumbs = Object.values(state.breadcrumbs)
        const updatedBreadcrumb = oldBreadcrumbs.find(
            ({ breadcrumb }) => breadcrumb.id === resourceId,
        )

        if (!!updatedBreadcrumb) {
            // we don't need application path here, as it is part of the tsx component
            return {
                ...state,
                breadcrumbs: {
                    ...state.breadcrumbs,
                    [updatedBreadcrumb.uuid]: {
                        ...updatedBreadcrumb,
                        breadcrumb: {
                            ...updatedBreadcrumb.breadcrumb,
                            workspaceName,

                            workspaceUrl: workspaceCoverPageUrl(
                                "",
                                workspaceId,
                            ),
                            listId: conceptId,
                            listName: conceptName,
                            listUrl: workspaceConceptUrl(
                                "",
                                workspaceId,
                                conceptId,
                            ),
                            url: workspaceItemUrl(
                                "",
                                workspaceId,
                                conceptId,
                                resourceId,
                            ),
                        },
                    },
                },
            }
        }

        return { ...state }
    },

    [actions.UPDATE_SHARESUCCESSMESSAGE_VISIBILITY]: (
        state,
        { visibility }: actions.UpdateVisibility,
    ): IHeader => ({
        ...state,
        shareSuccessMessageVisible: visibility,
    }),

    [actions.TOGGLE_BUTTON_SCRIPTS_VISIBILITY]: (
        state,
        { scriptIds }: actions.ToggleButtonScriptsVisibility,
    ): IHeader => {
        const buttonsVisibility: Record<string, boolean> = {
            ...state.buttonsVisibility,
        }

        scriptIds.forEach((scriptId) => {
            const id = `ListScript${scriptId}`

            const currentVisibility =
                // we need to make sure the false values gets processed properly
                // so we cannot use ??
                buttonsVisibility[id] !== undefined
                    ? buttonsVisibility[id]
                    : true
            buttonsVisibility[id] = !currentVisibility
        })

        return {
            ...state,
            buttonsVisibility,
        }
    },

    [actions.INCREASE_ATTACHMENTS_COUNT]: (
        state,
        { count }: actions.IncreaseCount,
    ): IHeader => {
        if (state.itemData && state.itemData.actions) {
            return {
                ...state,
                itemData: {
                    ...state.itemData,
                    actions: {
                        ...state.itemData.actions,
                        attachmentsCount:
                            state.itemData.actions.attachmentsCount + count,
                    },
                },
            }
        }
        return state
    },

    [actions.INCREASE_COMMENTS_COUNT]: (
        state,
        { count }: actions.IncreaseCount,
    ): IHeader => {
        if (state.itemData && state.itemData.actions) {
            return {
                ...state,
                itemData: {
                    ...state.itemData,
                    actions: {
                        ...state.itemData.actions,
                        commentsCount:
                            state.itemData.actions.commentsCount + count,
                    },
                },
            }
        }
        return state
    },

    [actions.UPDATE_BUTTON_VISIBILITY]: (
        state,
        { id, visibility }: actions.UpdateButtonVisibility,
    ): IHeader => {
        const buttonsVisibility: Record<string, boolean> = {
            ...state.buttonsVisibility,
        }

        buttonsVisibility[id] = visibility

        return {
            ...state,
            buttonsVisibility,
        }
    },

    [actions.UPDATE_BUTTON_LOADING]: (
        state,
        { buttonUuid, isLoading }: actions.UpdateButtonLoading,
    ): IHeader => {
        const buttons = {
            ...state.buttons,
            [buttonUuid]: {
                ...state.buttons[buttonUuid],
                isLoading,
            },
        }
        return {
            ...state,
            buttons,
        }
    },

    [actions.REQUEST_LIST_VIEWS]: (
        state,
        { selectionFilterId, viewType }: actions.RequestListViews,
    ): IHeader => ({
        ...state,
        changeViewData: {
            ...state.changeViewData,
            selectionId: selectionFilterId,
            viewType,
        },
    }),

    [actions.RECEIVE_LIST_VIEWS]: (
        state,
        { Values }: actions.ReceiveListViews,
    ): IHeader => ({
        ...state,
        changeViewData: {
            ...state.changeViewData,
            views: Values.length > 0 ? Values : state.changeViewData.views,
        },
    }),

    [actions.UPDATE_SELECTED_LIST_VIEW]: (
        state,
        { selectedView }: actions.UpdateSelectedListView,
    ): IHeader => ({
        ...state,
        changeViewData: {
            ...state.changeViewData,
            selectedView,
        },
    }),

    [actions.SET_BOOKMARK_UUID]: (
        state,
        { bookmarkButtonId }: actions.InitializeBookmarkButtonId,
    ): IHeader => ({
        ...state,
        resource: { ...state.resource, bookmarkButtonId },
    }),
}

export default function reducer(state = initialState, action: Action): IHeader {
    const handler: (state: IHeader, action: Action) => IHeader =
        ACTION_HANDLERS[action.type]
    return handler ? handler(state, action) : state
}
